import React, { useEffect, useState } from "react";
import { Button, Modal, Form } from "react-bootstrap";
import { Plus, X } from "lucide-react";
import swal from "sweetalert";
import { getmedicalhistory, updatemedicalhistory, addInjuryhistory } from "../../../../services/userApiService/userService";

const notify = (type, message) =>
    swal(type === "error" ? "Error" : "Success", message, type);

const MedicalHistory = ({ userData, token }) => {
    const [medicalData, setMedicalData] = useState(null);
    const [injuryList, setInjuryList] = useState([]);

    // Medical History Modal State
    const [showMedicalModal, setShowMedicalModal] = useState(false);
    const [medicalHistory, setMedicalHistory] = useState({
        height: "",
        weight: "",
        blood_group: "",
        age: ""
    });

    // Injury History Modal State
    const [showInjuryModal, setShowInjuryModal] = useState(false);
    const [currentInjury, setCurrentInjury] = useState({
        name: "",
        date: "",
        documents: [],
        existingDocs: []
    });
    const [documentInputs, setDocumentInputs] = useState([{ id: 1, file: null }]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const fetchMedicalData = async () => {
        try {
            const response = await getmedicalhistory();
            const data = response?.data?.data;

            if (data) {
                setMedicalData(data);
                setMedicalHistory({
                    height: data.height || "",
                    weight: data.weight || "",
                    blood_group: userData?.indivisual?.blood_group || "",
                    age: userData?.indivisual?.age || ""
                });

                let injuries = [];
                if (Array.isArray(data.injury_history)) {
                    injuries = data.injury_history;
                } else if (data.injury_history && typeof data.injury_history === 'object') {
                    injuries = Object.values(data.injury_history);
                } else if (Array.isArray(data.injuries)) {
                    injuries = data.injuries;
                }
                setInjuryList(injuries);
            } else {
                setInjuryList([]);
            }
        } catch (error) {
            console.error("Error fetching medical data:", error);
            setInjuryList([]);
        }
    };

    useEffect(() => {
        // Only fetch if userData is populated and token exists
        const currentToken = localStorage.getItem("access_token_admin");
        if (userData && Object.keys(userData).length > 0 && currentToken) {
            fetchMedicalData();
        }
    }, [userData]);

    // Medical History Modal Handlers
    const handleEditMedical = () => {
        if (medicalData) {
            setMedicalHistory({
                height: medicalData?.height || "",
                weight: medicalData?.weight || "",
                blood_group: medicalData?.blood_group || "",
                age: medicalData?.age || ""
            });
        }
        setShowMedicalModal(true);
    };

    const handleMedicalChange = (e) => {
        setMedicalHistory({
            ...medicalHistory,
            [e.target.name]: e.target.value
        });
    };

    const handleSaveMedical = async () => {
        if (!medicalHistory.height || !medicalHistory.weight) {
            notify("error", "Please fill all required fields");
            return;
        }

        try {
            setIsSubmitting(true);
            const formData = new FormData();
            formData.append("height", medicalHistory.height);
            formData.append("weight", medicalHistory.weight);
            formData.append("blood_group", medicalHistory.blood_group);
            formData.append("age", medicalHistory.age);

            const result = await updatemedicalhistory(formData);
            if (result.status) {
                notify("success", "Medical history updated successfully");
                setShowMedicalModal(false);
                await fetchMedicalData();
            } else {
                notify("error", result.message || "Failed to update medical history");
            }
        } catch (err) {
            console.error("Error updating medical history:", err);
            notify("error", "Failed to update medical history");
        } finally {
            setIsSubmitting(false);
        }
    };

    // Injury History Modal Handlers
    const handleAddInjury = () => {
        setCurrentInjury({
            name: "",
            date: "",
            documents: [],
            existingDocs: []
        });
        setDocumentInputs([{ id: 1, file: null }]);
        setShowInjuryModal(true);
    };

    const handleInjuryChange = (e) => {
        setCurrentInjury({
            ...currentInjury,
            [e.target.name]: e.target.value
        });
    };

    const handleDocumentChange = (id, file) => {
        setDocumentInputs(prev =>
            prev.map(input =>
                input.id === id ? { ...input, file } : input
            )
        );
    };

    const addDocumentInput = () => {
        if (documentInputs.length < 5) {
            setDocumentInputs([
                ...documentInputs,
                { id: documentInputs.length + 1, file: null }
            ]);
        }
    };

    const removeDocumentInput = (id) => {
        if (documentInputs.length > 1) {
            setDocumentInputs(documentInputs.filter(input => input.id !== id));
        }
    };

    const handleSaveInjury = async () => {
        if (!currentInjury.name || !currentInjury.date) {
            notify("error", "Please fill injury name and date");
            return;
        }

        try {
            setIsSubmitting(true);
            const formData = new FormData();

            const newIndex = injuryList.length;
            formData.append(`injury_history[${newIndex}][name]`, currentInjury.name);
            formData.append(`injury_history[${newIndex}][date]`, currentInjury.date);

            let docIndex = 0;
            documentInputs.forEach((input) => {
                if (input.file) {
                    formData.append(`injury_history[${newIndex}][documents][${docIndex}]`, input.file);
                    docIndex++;
                }
            });

            const result = await addInjuryhistory(formData);

            if (result?.data?.status || result?.status) {
                notify("success", "Injury history added successfully");
                setShowInjuryModal(false);
                await fetchMedicalData();
            } else {
                notify("error", result?.data?.message || result?.message || "Failed to add injury history");
            }
        } catch (err) {
            console.error("Error adding injury history:", err);
            notify("error", "Failed to add injury history");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="py-2">
            {/* BASIC MEDICAL INFO */}
            <div>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="fw-normal fs-4 mb-0">Basic Information</h6>
                    <Button variant="outline-dark" size="sm" className="px-3" style={{ borderColor: '#e5e7eb' }} onClick={handleEditMedical}>
                        Edit
                    </Button>
                </div>

                {medicalData ? (
                    <div className="row g-0">
                        <div className="col-md-3 col-sm-6 mb-3">
                            <label className="form-label text-muted small mb-1">Height</label>
                            <div className="fw-medium">{medicalData?.height || "-"} cm</div>
                        </div>
                        <div className="col-md-3 col-sm-6 mb-3">
                            <label className="form-label text-muted small mb-1">Weight</label>
                            <div className="fw-medium">{medicalData?.weight || "-"} kg</div>
                        </div>
                        <div className="col-md-3 col-sm-6 mb-3">
                            <label className="form-label text-muted small mb-1">Blood Group</label>
                            <div className="fw-medium">{medicalData?.blood_group || "-"}</div>
                        </div>
                        <div className="col-md-3 col-sm-6 mb-3">
                            <label className="form-label text-muted small mb-1">Age</label>
                            <div className="fw-medium">{medicalData?.age || "-"} years</div>
                        </div>
                    </div>
                ) : (
                    <div className="text-center py-4 text-muted border rounded  mb-3">
                        <p className="mb-0">No medical history available.</p>
                    </div>
                )}
            </div>

            <hr className="my-4 text-muted" style={{ opacity: 0.1 }} />

            {/* INJURY HISTORY */}
            <div>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="mb-0 fw-normal fs-4 ">Injury History</h6>
                </div>

                {injuryList && injuryList.length > 0 ? (
                    <div className="mt-3">
                        {injuryList.map((injury, index) => (
                            <div key={index} className="border rounded p-3 mb-3 bg-white">
                                <div className="d-flex justify-content-between align-items-start">
                                    <div>
                                        <h6 className="mb-1 fw-semibold">{injury.name || injury.injury_name || "Unnamed Injury"}</h6>
                                        <div className="text-muted small">Date: {injury.date ? new Date(injury.date).toLocaleDateString() : 'N/A'}</div>
                                    </div>
                                </div>
                                {injury.documents && injury.documents.length > 0 && (
                                    <div className="mt-2">
                                        <small className="text-muted d-block mb-1">Documents:</small>
                                        <div className="d-flex flex-wrap gap-2">
                                            {injury.documents.map((doc, idx) => (
                                                <a
                                                    key={idx}
                                                    href={doc.link || doc.url || doc.path}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="btn btn-sm btn-light border py-1 px-2"
                                                    style={{ fontSize: '12px' }}
                                                >
                                                    📄 Doc {idx + 1}
                                                </a>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                ) : null}

                {/* Add Button Area if empty or generally at bottom */}
                {(!injuryList || injuryList.length === 0) && (
                    <div className="text-center py-5  rounded border border-dashed">
                        <div className="text-muted small mb-3">No injury history recorded. Click "Add" to create a new entry.</div>
                        <Button variant="outline-dark" size="sm" onClick={handleAddInjury} className="px-4">
                            <Plus size={16} className="me-1" /> Add
                        </Button>
                    </div>
                )}

                {injuryList && injuryList.length > 0 && (
                    <div className="text-end mt-3">
                        <Button variant="outline-dark" size="sm" onClick={handleAddInjury}>
                            <Plus size={16} className="me-1" /> Add New Injury
                        </Button>
                    </div>
                )}
            </div>

            {/* MODALS (Same as before) */}
            <Modal show={showMedicalModal} onHide={() => setShowMedicalModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Edit Medical History</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <div className="row">
                            <div className="col-md-6 mb-3">
                                <Form.Group>
                                    <Form.Label>Height (cm) <span className="text-danger">*</span></Form.Label>
                                    <Form.Control type="number" name="height" value={medicalHistory.height} onChange={handleMedicalChange} required />
                                </Form.Group>
                            </div>
                            <div className="col-md-6 mb-3">
                                <Form.Group>
                                    <Form.Label>Weight (kg) <span className="text-danger">*</span></Form.Label>
                                    <Form.Control type="number" name="weight" value={medicalHistory.weight} onChange={handleMedicalChange} required />
                                </Form.Group>
                            </div>
                            <div className="col-md-6 mb-3">
                                <Form.Group>
                                    <Form.Label>Blood Group</Form.Label>
                                    <Form.Control type="text" name="blood_group" value={medicalHistory.blood_group} onChange={handleMedicalChange} />
                                </Form.Group>
                            </div>
                            <div className="col-md-6 mb-3">
                                <Form.Group>
                                    <Form.Label>Age</Form.Label>
                                    <Form.Control type="text" name="age" value={medicalHistory.age} onChange={handleMedicalChange} />
                                </Form.Group>
                            </div>
                        </div>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowMedicalModal(false)}>Cancel</Button>
                    <Button variant="success" onClick={handleSaveMedical} disabled={isSubmitting}>
                        {isSubmitting ? "Saving..." : "Save Changes"}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showInjuryModal} onHide={() => setShowInjuryModal(false)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Add Injury</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <div className="row">
                            <div className="col-md-6 mb-3">
                                <Form.Label>Date <span className="text-danger">*</span></Form.Label>
                                <Form.Control type="date" name="date" value={currentInjury.date} onChange={handleInjuryChange} max={new Date().toISOString().split('T')[0]} />
                            </div>
                            <div className="col-md-6 mb-3">
                                <Form.Label>Injury Name <span className="text-danger">*</span></Form.Label>
                                <Form.Control type="text" name="name" value={currentInjury.name} onChange={handleInjuryChange} placeholder="e.g. Knee Sprain" />
                            </div>
                        </div>
                        <div className="mb-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <Form.Label className="mb-0">Documents (Max 5)</Form.Label>
                                {documentInputs.length < 5 && (
                                    <Button variant="outline-primary" size="sm" onClick={addDocumentInput}>
                                        <Plus size={14} className="me-1" /> Add
                                    </Button>
                                )}
                            </div>
                            {documentInputs.map((input) => (
                                <div key={input.id} className="d-flex gap-2 mb-2">
                                    <Form.Control type="file" onChange={(e) => handleDocumentChange(input.id, e.target.files[0])} accept="image/*,.pdf" />
                                    {documentInputs.length > 1 && (
                                        <Button variant="outline-danger" size="sm" onClick={() => removeDocumentInput(input.id)}><X size={16} /></Button>
                                    )}
                                </div>
                            ))}
                        </div>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowInjuryModal(false)}>Cancel</Button>
                    <Button variant="success"
                        onClick={handleSaveInjury}
                        disabled={isSubmitting}>
                        {isSubmitting ? "Saving..." : "Submit"}
                    </Button>
                </Modal.Footer>
            </Modal>

        </div>
    );
};

export default MedicalHistory;
